import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Scissors, Store, ShoppingBag, Home as HomeIcon, Star, Heart, Crown, Zap, Coffee, Stethoscope, Gift, Wallet, Check, CalendarDays, MessageCircle, Share, Sparkles, Lock } from "lucide-react";
import LoyaltyService from "../../services/loyalty/LoyaltyService";
import "../admin-dashboard/panel.css";
import "./public.css";
import "./loyalty-card.css";

const ICONS = { Scissors, Store, ShoppingBag, Home: HomeIcon, HomeIcon, Star, Heart, Crown, Zap, Coffee, Stethoscope };
const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

/**
 * Tarjeta de lealtad del cliente final: /:slug/tarjeta/:token. Sin sesión: la llave es el
 * token opaco del link. Misma marca que la agenda (cp-root con el color del negocio).
 * "Agregar a Google Wallet" solo si el backend tiene Wallet configurado; en iPhone se
 * sugiere agregarla a la pantalla de inicio.
 *
 * Dos estados distintos que no se mezclan: `program.enabled: false` es que el negocio
 * pausó su programa (sigue pagando); `addonActive: false` es que ya no tiene el add-on o
 * su suscripción venció. En el segundo la tarjeta es solo lectura: visitas y vales se
 * ven, pero no hay acciones (Wallet) ni promesas ("te faltan N", "cómo funciona").
 */
export default function LoyaltyCardPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    document.body.classList.add("js-loaded");
    const loader = document.getElementById("initial-loader");
    if (loader) loader.remove();
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await LoyaltyService.getPublicCard(token);
        if (!alive) return;
        setData(d); setStatus("ok");
        document.title = `Tu tarjeta · ${d.business.name}`;
      } catch { if (alive) setStatus("notfound"); }
    })();
    return () => { alive = false; };
  }, [token]);

  // "Agregar a inicio" en iPhone: nombre del negocio e ícono de tarjeta en vez de los
  // de la agenda. Safari no lee manifest: usa las metas apple-* y el apple-touch-icon
  // del <head>, así que se cambian mientras esta página está montada y se restauran
  // al salir. Android no necesita nada: ahí la tarjeta va en Google Wallet.
  useEffect(() => {
    const name = data?.business?.name;
    if (!name) return undefined;
    const icon = document.querySelector('link[rel="apple-touch-icon"]');
    const prevIcon = icon?.getAttribute("href");
    icon?.setAttribute("href", "/loyalty-icon-180.png");
    const metas = [
      ["apple-mobile-web-app-capable", "yes"],
      ["apple-mobile-web-app-title", name],
      ["apple-mobile-web-app-status-bar-style", "default"],
    ].map(([n, content]) => {
      const m = document.createElement("meta");
      m.name = n; m.content = content;
      document.head.appendChild(m);
      return m;
    });
    return () => {
      if (icon && prevIcon) icon.setAttribute("href", prevIcon);
      metas.forEach((m) => m.remove());
    };
  }, [data?.business?.name]);

  const brandStyle = useMemo(() => {
    const p = data?.business || {};
    return p.primaryColor ? { "--primary-color": p.primaryColor, "--primary-dark": p.primaryDark || p.primaryColor } : undefined;
  }, [data]);

  const Icon = ICONS[data?.business?.selectedIcon] || Scissors;
  const brandIcon = data?.business?.logoType === "image" && data.business.logoImage ? <img src={data.business.logoImage} alt="" /> : <Icon size={24} />;

  return (
    <div className="cp-root cp-public cp-loycard" style={brandStyle}>
      <header className="cp-topbar" translate="no">
        <div className="cp-topbar-inner">
          <div className="cp-brand">
            <span className="cp-brand-icon">{brandIcon}</span>
            <span className="cp-brand-name">{data?.business?.name || "Tarjeta de lealtad"}</span>
          </div>
          {data?.business?.agendaUrl && (
            <div className="cp-topbar-actions">
              <a className="cp-btn" href={data.business.agendaUrl}><CalendarDays size={16} /> <span className="cp-btn-label">Agendar</span></a>
            </div>
          )}
        </div>
      </header>

      <main className="cp-public-main">
        {status === "loading" && (
          <div className="cp-stack"><div className="cp-skeleton" style={{ height: 28, width: 160 }} /><div className="cp-skeleton" style={{ height: 220 }} /><div className="cp-skeleton" style={{ height: 48 }} /></div>
        )}
        {status === "notfound" && (
          <div className="cp-card"><div className="cp-empty"><Gift size={32} /><h3>Esta tarjeta no existe</h3><p>Puede que el link haya cambiado. Pide al negocio que te lo vuelva a mandar por WhatsApp.</p></div></div>
        )}
        {status === "ok" && data && <CardBody data={data} token={token} />}
      </main>

      <footer className="cp-loycard-foot">Tarjeta digital por <a href="https://turnlink.app" target="_blank" rel="noreferrer">TurnLink</a></footer>
    </div>
  );
}

function CardBody({ data, token }) {
  const { business, program, card, walletAvailable } = data;
  // Respuestas anteriores al campo no lo traen: se asume activo para no marcar de más.
  const readOnly = data.addonActive === false;
  const max = program.maxVisits || 0;
  const stampCount = Math.min(max, 30);
  const tierAt = (n) => program.tiers.find((t) => t.visits === n);
  const ios = isIos();
  const hasPending = card.pendingRewards.length > 0;

  return (
    <div className="cp-loycard-body">
      <div>
        <p className="cp-eyebrow">Tu tarjeta de lealtad</p>
        <h1 className="cp-loycard-title">{card.name ? `Hola, ${card.name}` : "Hola"}</h1>
      </div>

      {/* El link es la llave de la tarjeta (no hay login): quien lo tenga la ve. Aviso arriba, donde se lee. */}
      <div className="cp-note cp-note-accent cp-loycard-private">
        <Lock size={16} />
        <div><b>Este link es solo tuyo.</b> No lo compartas ni lo reenvíes: cualquiera que lo abra verá tu tarjeta y tus recompensas.</div>
      </div>

      {readOnly ? (
        <div className="cp-note"><Gift size={16} /><div><b>El programa de lealtad de {business.name} no está disponible por ahora.</b> Tus visitas y recompensas se conservan; si el negocio lo vuelve a activar, las verás aquí tal cual.</div></div>
      ) : !program.enabled && (
        <div className="cp-note"><Gift size={16} /><div>El programa está en pausa. Tus visitas y recompensas se conservan.</div></div>
      )}

      {/* Tarjeta con sellos */}
      <section className="cp-loycard-card" aria-label="Visitas">
        <div className="cp-loycard-head">
          <div>
            <div className="cp-loycard-label">Visitas</div>
            <div className="cp-loycard-big">{card.completed ? <Check size={30} strokeWidth={3} /> : card.visits} {!card.completed && max > 0 && <small>de {max}</small>}</div>
          </div>
          {hasPending && <span className="cp-loycard-pill"><Gift size={14} /> {card.pendingRewards.length === 1 ? "1 recompensa" : `${card.pendingRewards.length} recompensas`}</span>}
        </div>
        {stampCount > 0 && (
          <div className={`cp-loycard-stamps ${stampCount > 12 ? "is-dense" : ""}`}>
            {Array.from({ length: stampCount }, (_, i) => i + 1).map((n) => {
              const filled = card.completed || n <= card.visits;
              const tier = tierAt(n);
              return (
                <span key={n} className={`cp-loycard-stamp ${filled ? "is-on" : ""} ${tier ? "is-tier" : ""}`} title={tier ? tier.title : `Visita ${n}`}>
                  {filled ? (tier ? <Gift size={18} /> : <Check size={18} strokeWidth={3} />) : tier ? <Gift size={16} /> : <span>{n}</span>}
                </span>
              );
            })}
          </div>
        )}
        <div className="cp-loycard-next">
          {readOnly ? "Tus visitas se conservan."
            : card.completed ? "Completaste el programa. ¡Gracias por tu preferencia!"
            : card.nextTier ? `Te faltan ${card.nextTier.visits - card.visits} ${card.nextTier.visits - card.visits === 1 ? "visita" : "visitas"} para ${card.nextTier.title}`
            : "Sigue sumando visitas"}
        </div>
      </section>

      {hasPending && (
        <section className="cp-card cp-loycard-rewards">
          <h2 className="cp-card-title"><Sparkles size={18} /> Por canjear</h2>
          <ul className="cp-list">
            {card.pendingRewards.map((r) => (
              <li key={r.id} className="cp-row">
                <span className="cp-loycard-reward-icon"><Gift size={18} /></span>
                <span className="cp-row-main"><span className="cp-row-title">{r.title}</span><span className="cp-row-sub">{readOnly ? "Se conserva" : "Muéstrala en tu siguiente visita"}</span></span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Guardar en el celular */}
      {walletAvailable && (
        <div className="cp-loycard-save">
          <a className="cp-loycard-wallet" href={LoyaltyService.publicWalletUrl(token)}><Wallet size={20} /> Agregar a Google Wallet</a>
          {ios && <p className="cp-hint">¿Tienes iPhone? Toca <Share size={12} style={{ verticalAlign: -1 }} /> <b>Compartir</b> y luego <b>Agregar a inicio</b> para tenerla a la mano.</p>}
        </div>
      )}
      {!walletAvailable && !readOnly && ios && (
        <p className="cp-hint cp-loycard-center">Toca <Share size={12} style={{ verticalAlign: -1 }} /> <b>Compartir</b> y luego <b>Agregar a inicio</b> para tener tu tarjeta a la mano.</p>
      )}

      {/* Cómo funciona. Sin QR: las visitas las registra el negocio desde su agenda, no escaneando.
          En solo lectura se oculta: "cada cita suma una visita" ya no es cierto. */}
      {!readOnly && (
      <section className="cp-card cp-loycard-how">
        <div>
          <h2 className="cp-card-title">Cómo funciona</h2>
          <p className="cp-muted">Cada cita atendida suma una visita.</p>
          <ul className="cp-loycard-tiers">
            {program.tiers.map((t) => <li key={t.visits}><b>{t.visits}</b> visitas: {t.title}</li>)}
          </ul>
          {program.repeatCycle && program.tiers.length > 0 && <p className="cp-hint">Al completar el programa vuelves a empezar.</p>}
        </div>
      </section>
      )}

      <div className="cp-loycard-actions">
        {business.agendaUrl && <a className="cp-btn cp-btn-primary" href={business.agendaUrl}><CalendarDays size={16} /> Agendar mi siguiente cita</a>}
        {business.whatsapp && <a className="cp-btn" href={`https://wa.me/${String(business.whatsapp).replace(/\D/g, "")}`} target="_blank" rel="noreferrer"><MessageCircle size={16} /> WhatsApp del negocio</a>}
      </div>
    </div>
  );
}
